import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery, useMutation } from "@apollo/client";
import get from "lodash/get";

import useTimeout from "shared/hooks/useTimeout";
import Toast from "shared/components/Toast";
import _str from "shared/util/_str";
import getImagePath from "shared/util/getImagePath";
import { isDesktop } from "shared/util/EnvironmentHelpers";
import Context from "shared/util/Context";
import useIsWhitelistedNotificationRoute from "apps/notifications_footer/util/useIsWhitelistedNotificationRoute";
import { AUTHED_ROUTES } from "routers/shared/util/consts";
import {
	REGRET_SWIPE_ALERT_QUERY,
	REGRET_SWIPE_ALERT_MUTATION,
} from "./queries";
import {
	dismissedPromoDriverEvent,
	selectedPromoDriverEvent,
	viewedPromoDriverEvent,
	passedOnPotentialMatchEvent,
} from "./analytics";

import HeartIcon from "./heart.svg";
import "./RegretSwipeAlert.scss";

const OPEN_REGRET_SWIPE_TIMEOUT = 30000;
const CLOSE_REGRET_SWIPE_TIMEOUT = OPEN_REGRET_SWIPE_TIMEOUT + 8000;
const IMAGE_URL = `${getImagePath()}media/img/toasts/regret_swipe_${isDesktop ? "desktop" : "mw"}.png`;

const Icon = () => (
	<div className="regret-swipe-alert-icon">
		<img
			alt="regret-swipe"
			src={IMAGE_URL}
		/>
		<HeartIcon className="regret-swipe-alert-icon-heart" />
	</div>
);

const RegretSwipeAlert = ({ isOpen, onClose }) => {
	const [expires, setExpires] = useState(null);
	const [hasSeen, setHasSeen] = useState(true);
	const [shouldShow, setShouldShow] = useState(false);
	const isAlertRouteWhitelisted = useIsWhitelistedNotificationRoute();

	const shouldRegretSwipeOpen = shouldShow && isAlertRouteWhitelisted;

	const { data: regretSwipeData } = useQuery(REGRET_SWIPE_ALERT_QUERY, {
		variables: {
			userId: Context.get("userid"),
		},
		skip: Boolean(expires) && Date.now() > expires,
	});

	const incomingLikesCount = get(regretSwipeData, "user.notificationCounts.likesIncoming") || 0;

	useEffect(() => {
		if (regretSwipeData) {
			const regretSwipeAlert = get(regretSwipeData, "user.likesHasSeenRegretSwipeAlert") || {};
			setExpires(regretSwipeAlert.expires);
			setHasSeen(regretSwipeAlert.hasSeen);
		}
	}, [regretSwipeData]);

	const [updateHasSeenRegretSwipe] = useMutation(REGRET_SWIPE_ALERT_MUTATION);

	const openAlertTimeout = useTimeout({
		delay: OPEN_REGRET_SWIPE_TIMEOUT,
		callback: () => {
			setShouldShow(true);
			updateHasSeenRegretSwipe();
			setExpires(Date.now());
			setHasSeen(true);
			passedOnPotentialMatchEvent(incomingLikesCount);
		},
	});

	const closeAlertTimeout = useTimeout({
		delay: CLOSE_REGRET_SWIPE_TIMEOUT,
		callback: () => {
			setShouldShow(false);
			onClose();
		},
	});

	useEffect(() => {
		if (isOpen && !hasSeen) {
			openAlertTimeout.start();
			closeAlertTimeout.start();
		}

		return () => {
			openAlertTimeout.stop();
			closeAlertTimeout.stop();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);


	useEffect(() => {
		if (shouldRegretSwipeOpen) {
			viewedPromoDriverEvent(incomingLikesCount);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldRegretSwipeOpen]);

	if (!shouldRegretSwipeOpen) return null;

	return (
		<Toast
			image={<Icon />}
			header={_str("Oops! You missed a match!")}
			subHeader={_str("Upgrade to see who likes you")}
			shouldAnimate
			onClose={() => {
				dismissedPromoDriverEvent(incomingLikesCount);
				setShouldShow(false);
				onClose();
			}}
			onClick={() => {
				selectedPromoDriverEvent(incomingLikesCount);
			}}
			className="regret-swipe-alert"
			linkTo={{
				pathname: AUTHED_ROUTES.WhoLikesYou[0],
				state: { driver: "inapp regret swipe" },
			}}
		/>
	);
};

RegretSwipeAlert.propTypes = {
	onClose: PropTypes.func.isRequired,
	isOpen: PropTypes.bool,
};

RegretSwipeAlert.defaultProps = {
	isOpen: false,
};

export default RegretSwipeAlert;



// WEBPACK FOOTER //
// ./src/shared/components/RegretSwipeAlert/RegretSwipeAlert.jsx